import React, { useEffect, useState, useMemo } from 'react';
import cx from 'classnames';
import { createBrowserHistory } from 'history';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';

import { createTheme, makeStyles, ThemeProvider } from '@material-ui/core/styles';
import ScopedCssBaseline from '@material-ui/core/ScopedCssBaseline';

import { AAG_SOD_BASENAME, AAG_LICENSE_INTELLIGENCE_BASENAME } from '@saviynt/aag';
import { SAVI_ASSISTANT_BASENAME, SERVICE_IDENTITY_LIST } from '@saviynt/savicopilot';
import { usePendo } from '@saviynt/common';

import { LOCALE } from 'ui-common/src/constants';
import { appMessages, getIsMenuPinnedSuccess } from 'ui-common/src/utils/LoginActions';

import { InitializeSideMenu } from '../actions/sideMenuAction';
import {
  InitializeGetHelp,
  InitializeGetIspmIntro,
  InitializeProfileMenu,
} from '../actions/profileMenuAction';
import {
  getActiveFavIcon,
  getActiveHeaderLogo,
  getActiveLogo,
} from '../features/AdminHome/redux/adminHome-actions';
import { useTheme } from '../features/AdminHome/views/BrandingConfig/utils/helper';

import Header from './Header/Header';
import styles from './Layout.module.css';
import SideMenu from './SideMenu/SideMenu';

const ROUTE_PREFIXES_TO_EXCLUDE_LAYOUT_MAIN = [
  '/ECMv6/certification/details',
  '/ECMv6/certification/list',
  `/ECMv6${AAG_SOD_BASENAME}`,
  `/ECMv6${AAG_LICENSE_INTELLIGENCE_BASENAME}`,
  `/ECMv6${SAVI_ASSISTANT_BASENAME}`,
  `/ECMv6${SERVICE_IDENTITY_LIST}`,
];

const history = createBrowserHistory({ window });
const DESKTOP_DRAWER_WIDTH = 240;
const MOBILE_DRAWER_WIDTH = 280;
export const IS_MENU_PINNED_KEY = 'gen2.layout.isMenuPinned';
const SLIDE_DURATION = 300;
const theme = createTheme({
  palette: {
    primary: {
      main: 'rgb(55, 81, 255, 1)',
      contrastText: '#fff',
    },
    typography: {
      fontFamily: 'Rubik, sans-serif',
      fontSize: 12,
    },
  },
  overrides: {
    MuiButton: {
      root: {
        textTransform: 'capitalize',
        lineHeight: 'inherit',
        border: 'none',
        borderRadius: 3,
        fontSize: 12,
        textAlign: 'center',
        letterSpacing: 'normal',
        fontStretch: 'normal',
        fontStyle: 'normal',
      },
      contained: {
        padding: '9px 15px',
      },
    },
  },
});

const SIDE_MENU_Z_INDEX = 1010; // play nicely with GSP Bootstrap v3 tooltips

export const useStyles = makeStyles((theme) => ({
  sideMenuIcon: {
    paddingRight: '22px',
  },
  appBar: {
    zIndex: SIDE_MENU_Z_INDEX,
    boxShadow: 'rgb(0 0 0 / 6%) 0 4px 12px',
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    marginLeft: DESKTOP_DRAWER_WIDTH,
    width: `calc(100% - ${DESKTOP_DRAWER_WIDTH}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginLeft: 0,
  },
  hide: {
    display: 'none',
  },
  drawer: {
    width: DESKTOP_DRAWER_WIDTH,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  drawerOpen: {
    width: DESKTOP_DRAWER_WIDTH,
    // background: 'var(--SIDEMENU-BACKGROUND)',
    color: 'var(--SIDEMENU-TEXT-COLOR)',
    overflow: 'hidden',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      // duration: theme.transitions.duration.enteringScreen,
      duration: `${SLIDE_DURATION}ms`,
    }),
    background: 'inherit',
    zIndex: SIDE_MENU_Z_INDEX,
  },
  drawerClose: {
    // background: 'var(--SIDEMENU-BACKGROUND)',
    color: 'var(--SIDEMENU-COLOR)',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      // duration: theme.transitions.duration.leavingScreen,
      duration: `${SLIDE_DURATION}ms`,
    }),
    overflowX: 'hidden',
    overflowY: 'hidden',
    width: theme.spacing(7) + 1,
    [theme.breakpoints.up('sm')]: {
      width: 60,
    },
    background: 'inherit',
    zIndex: SIDE_MENU_Z_INDEX,
  },
  toolbar: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    ...theme.mixins.toolbar,
  },
  toolbarCentered: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    ...theme.mixins.toolbar,
  },
  toolbarMobileCentered: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    ...theme.mixins.toolbar,
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  navBar: {
    paddingTop: 0,
    flex: '1 1 auto',
    overflow: 'scroll',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    '&::-moz-scrollbar': {
      display: 'none',
    },
    scrollbarWidth: 'none',
    scrollbarColor: 'transparent transparent',
  },
  searchText: {
    display: 'block',
    margin: '8px 16px',
    color: 'var(--SIDEMENU-SUBTEXT-COLOR)',
  },
  leftNavigationFooter: {
    flex: '0 0 auto',
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '1rem',
    color: 'var(--SIDEMENU-TEXT-COLOR)',
    background: 'var(--SIDEMENU-BACKGROUND)',
    padding: 0,
    overflow: 'hidden',
    opacity: '0',
    whiteSpace: 'break-spaces',
    height: '90px',
    wordBreak: 'break-word',
    textAlign: 'center',
  },
  leftNavigationFooterHide: {
    width: 0,
    padding: 0,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      // duration: theme.transitions.duration.leavingScreen,
      duration: '300ms',
    }),
  },
  leftNavigationFooterShow: {
    width: 'inherit',
    padding: '1rem',
    opacity: '1',
  },
  notMobileFooter: {
    overflow: 'hidden',
  },
  mobileDrawer: {
    width: MOBILE_DRAWER_WIDTH,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    overflowY: 'hidden',
    background: 'var(--SIDEMENU-BACKGROUND)',
    // background: getBackgroundColor(),
    color: 'var(--SIDEMENU-COLOR)',
  },
}));

const addSideMenuClasses = (sideMenu, isMenuPinned) => {
  const ecmLayout = window.document.getElementById('pageLayout');
  if (ecmLayout) {
    if (!sideMenu) {
      ecmLayout.setAttribute('class', 'LayoutWithoutSideNav');
    } else if (sideMenu && isMenuPinned) {
      ecmLayout.setAttribute('class', 'LayoutWithSideNavMenuOpen');
    } else if (sideMenu && !isMenuPinned) {
      ecmLayout.setAttribute('class', 'LayoutWithSideNav');
    }
  }
};

function setMenuScrollPosition() {
  // get reference to the left nav, not the header nav.
  const allNavs = Array.from(document.querySelectorAll('nav[class^="MuiList-root"]'));
  const scrollContainer = allNavs?.filter(
    (nav) => !nav.closest('div[class^="Header_profileMainContainer"]')
  )[0];
  const selectedIndicator = scrollContainer?.querySelector('[class^="MenuItem_selected"]');
  const selectedItem = selectedIndicator ? selectedIndicator.nextElementSibling : null;

  if (!selectedItem) {
    return;
  }

  selectedItem.scrollIntoView({ behavior: 'instant', block: 'center' });
  selectedItem.closest('div[class^="MuiPaper-root"]').scrollTop = 0;
}

const Layout = ({ children, isVisible = true, hideSideMenu = false }) => {
  // ===== Basics / hooks =====
  const dispatch = useDispatch();
  const classes = useStyles(theme);
  usePendo();
  const { setThemeVars } = useTheme();

  // ===== Routing / URL flags =====
  const pathName = window.location.pathname;
  const isExcludedRoute = ROUTE_PREFIXES_TO_EXCLUDE_LAYOUT_MAIN.some((p) => pathName.startsWith(p));
  const isAgentConfigurationPage = pathName.includes('ECMv6/agent/configuration');
  const hideSideNav = isAgentConfigurationPage;

  const mainClasses = cx(!isExcludedRoute && classes.content);

  // ===== Redux selectors =====
  const sideMenuData = useSelector((s) => s.app?.sideMenu);
  const isMenuPinned = useSelector((s) => s.user?.isMenuPinned);
  const activeTheme = useSelector((s) => s.adminHome?.brandingConfig?.activeTheme) || {};
  const locale = useSelector((s) => s.user?.userInfo?.userSavRoleMap?.locale);
  const gatewayApiUrl = useSelector((s) => s.user?.domainInfo?.GATEWAY_API_URL);
  const {
    PAM_EMERGENCY_ROLE_NAV_ENABLED: isEmergencyRolesUser,
    INTEGRATION_SERVICE_ENABLED: isIntegration,
    ISPM_SERVICES_ENABLED: isIspmEnabled,
  } = useSelector((s) => s.user?.domainInfo) || {};
  const currentTheme = useSelector((s) => s.theme?.currentTheme || 'light');
  const userRoles = useSelector((s) => s.user?.userInfo?.roles) || [];
  const { OMNIFLOW_ENABLED: isOmniflowEnabled } = useSelector(
    (s) =>
      s.user?.agentRequestConfig || {
        OMNIFLOW_ENABLED: null,
      }
  );

  // ===== Local state =====
  const [expandSideMenu, setExpandSideMenu] = useState(isMenuPinned);
  const [collapseSideMenu, setCollapseSideMenu] = useState(!isMenuPinned);
  const [mobileDrawer, setMobileDrawer] = useState(false);
  const [showFooter, setShowFooter] = useState(isMenuPinned);

  // ===== Derived values =====
  const sideMenuAvailable = Boolean(sideMenuData && sideMenuData.length > 0);
  const showSideMenu = hideSideMenu || sideMenuAvailable;

  const featureFlags = useMemo(
    () => ({
      isEmergencyRolesUser,
      isIntegration,
      isIspmEnabled,
      isAgentRequestEnabled: isOmniflowEnabled,
    }),
    [isEmergencyRolesUser, isIntegration, isIspmEnabled, isOmniflowEnabled]
  );

  // ===== Effects =====
  // keep body layout class in sync with side menu state
  useEffect(() => {
    addSideMenuClasses(sideMenuAvailable, isMenuPinned);
  }, [sideMenuAvailable, isMenuPinned]);

  // sync expand/collapse with isMenuPinned changes
  useEffect(() => {
    setExpandSideMenu(isMenuPinned);
    setCollapseSideMenu(!isMenuPinned);
  }, [isMenuPinned]);

  // one-time init: listeners, profiles, help, ISPM intro, theme vars, hide footer
  useEffect(() => {
    dispatch(InitializeProfileMenu());
    dispatch(InitializeGetHelp());

    const eligibleForIspmIntro =
      isIspmEnabled &&
      (userRoles.includes('ROLE_ADMIN') ||
        userRoles.includes('ROLE_SAV_ISPMADMIN') ||
        userRoles.includes('ROLE_SAV_ISPMVIEWER'));

    if (eligibleForIspmIntro) {
      dispatch(InitializeGetIspmIntro());
    }

    setThemeVars();

    const footer = document.querySelector('.footer');
    if (footer) {
      footer.style.visibility = 'hidden';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentional: run once at mount

  // update theme assets when theme changes
  useEffect(() => {
    if (activeTheme) {
      setThemeVars();
      dispatch(getActiveLogo());
      dispatch(getActiveHeaderLogo());
      dispatch(getActiveFavIcon());
    }
  }, [activeTheme, currentTheme]);

  // after side menu shows, restore scroll position if pinned
  useEffect(() => {
    if (isMenuPinned) {
      setTimeout(() => setMenuScrollPosition(), 0);
    }
  }, [showSideMenu]);

  useEffect(() => {
    // Show the correct language to the user, and cache preferred language in
    // localStorage.

    // On page load, we retrieve translations in language specified in
    // localStorage. If none is specified, we optimistically start retrieving
    // English. As page load continues, we retrieve user's preferred language
    // (`locale`) from the server.

    // If preferred language differs from what we have in localStorage, we
    // update localStorage and retrieve translations in preferred language. If
    // preferred language is `null` (meaning they have never chosen a language),
    // we retrieve English - which may have already been loaded and results in a
    // noop.

    const stored = localStorage.getItem(LOCALE);
    const shouldUpdate = gatewayApiUrl && locale !== undefined && stored !== locale;

    if (shouldUpdate) {
      localStorage.setItem(LOCALE, locale);
      dispatch(appMessages(locale ?? 'en', gatewayApiUrl));
    }
  }, [locale, gatewayApiUrl]);

  // Set RTL direction for Arabic locale
  useEffect(() => {
    const currentLocale = locale ?? localStorage.getItem(LOCALE) ?? 'en';
    const dir = currentLocale === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', currentLocale);
  }, [locale]);

  useEffect(() => {
    const featureFlagsAvailable = Object.values(featureFlags).every(
      (flag) => flag != null && flag != undefined
    );

    if (!sideMenuAvailable && featureFlagsAvailable) {
      dispatch(InitializeSideMenu(featureFlags));
    }
  }, [featureFlags, sideMenuAvailable]);

  // ===== Handlers =====
  const handleDrawerOpen = () => {
    setExpandSideMenu(true);
    setCollapseSideMenu(false);
    setShowFooter(true);
    if (!isMenuPinned) {
      setTimeout(() => setMenuScrollPosition(), 0);
    }
  };

  const handleDrawerClose = () => {
    setExpandSideMenu(false);
    setCollapseSideMenu(true);
    setShowFooter(false);
  };

  // on click of toggle button
  const handleDrawerClick = () => {
    if (isMenuPinned) {
      handleDrawerClose();
    }
    dispatch(getIsMenuPinnedSuccess({ isMenuPinned: !isMenuPinned }));
  };

  const handleMobileDrawerClick = () => {
    setMobileDrawer(!mobileDrawer);
    setExpandSideMenu(true);
    setTimeout(() => setMenuScrollPosition(), 0);
  };

  const handleSearchInactive = () => {
    setTimeout(() => setMenuScrollPosition(), 0);
  };

  // ===== Render =====
  return (
    <BrowserRouter history={history}>
      <div className={cx(styles.page)}>
        <ScopedCssBaseline>
          <ThemeProvider theme={theme}>
            {isVisible && (
              <Header
                classes={classes}
                isSideMenuAvailable={showSideMenu}
                showLogo={!expandSideMenu && !isMenuPinned}
                isVisible
                handleMobileDrawerClick={handleMobileDrawerClick}
                isMenuPinned={isMenuPinned}
                className="EicAppHeader"
              />
            )}
            {showSideMenu && isVisible && !hideSideNav && (
              <SideMenu
                classes={classes}
                expandSideMenu={expandSideMenu || isMenuPinned}
                collapseSideMenu={collapseSideMenu && !isMenuPinned}
                isSideMenuAvailable={showSideMenu}
                handleDrawerClick={handleDrawerClick}
                handleDrawerOpen={handleDrawerOpen}
                handleDrawerClose={handleDrawerClose}
                location={`${window.location.pathname}${window.location.search}`}
                mobileDrawer={mobileDrawer}
                handleMobileDrawerClick={handleMobileDrawerClick}
                showFooter={showFooter || isMenuPinned}
                slideDuration={SLIDE_DURATION}
                handleSearchInactive={handleSearchInactive}
              />
            )}
          </ThemeProvider>
        </ScopedCssBaseline>
        <main className={mainClasses}>
          <div>{children}</div>
        </main>
      </div>
    </BrowserRouter>
  );
};

Layout.propTypes = {
  isVisible: PropTypes.bool,
  hideSideMenu: PropTypes.bool,
};

export default Layout;
